// ==============================================
// DNACORE 2.0 - MÓDULO CONFIGURAÇÕES
// Administração e configurações do sistema
// ==============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings,
  Users,
  Shield,
  Building2,
  Calendar,
  Layers,
  BookOpen,
  CreditCard,
  Receipt,
  FileText,
  Link2,
  History,
  Search,
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Check,
  X,
  ChevronRight,
  Key,
  Mail,
  Phone,
  Lock,
  Unlock,
  UserPlus,
  UserCog,
  ToggleLeft,
  ToggleRight,
  Database,
  Cloud,
  Webhook,
  AlertCircle,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  Download,
  Upload,
  MoreHorizontal,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Switch } from '../../../components/ui/switch';
import { Label } from '../../../components/ui/label';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';

const API_URL = window.location.origin || '';

// ================== COMPONENTES AUXILIARES ==================

// Card de Estatística de Configuração
const ConfigStatCard = ({ title, value, icon: Icon, color = 'violet' }) => {
  const colorClasses = {
    violet: 'bg-violet-100 text-violet-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    blue: 'bg-blue-100 text-blue-600',
    rose: 'bg-rose-100 text-rose-600'
  };

  return (
    <div className={cn("p-4 rounded-xl", colorClasses[color])}>
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5" />
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm opacity-80">{title}</p>
        </div>
      </div>
    </div>
  );
};

// Card de Usuário
const UsuarioCard = ({ usuario, onEdit, onToggleStatus, onResetPassword }) => {
  const perfilColors = {
    diretoria: 'bg-violet-100 text-violet-700',
    financeiro: 'bg-emerald-100 text-emerald-700',
    coordenacao: 'bg-blue-100 text-blue-700',
    professor: 'bg-amber-100 text-amber-700',
    secretaria: 'bg-rose-100 text-rose-700',
    admin: 'bg-slate-100 text-slate-700'
  };

  return (
    <Card className="hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold">
            {usuario.nome?.charAt(0) || 'U'}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-slate-800 truncate">{usuario.nome}</h4>
              <Badge className={perfilColors[usuario.perfil] || perfilColors.admin}>
                {usuario.perfil}
              </Badge>
              {!usuario.ativo && (
                <Badge variant="outline" className="border-rose-300 text-rose-700">Inativo</Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 truncate">{usuario.email}</p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => onEdit?.(usuario)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onResetPassword?.(usuario)}>
              <Key className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => onToggleStatus?.(usuario)}
              className={usuario.ativo ? "text-emerald-600" : "text-slate-400"}
            >
              {usuario.ativo ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Card de Perfil/Permissão
const PerfilCard = ({ perfil, onEdit, onView }) => {
  return (
    <Card className="hover:shadow-md transition-all cursor-pointer" onClick={() => onView?.(perfil)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-5 w-5 text-violet-600" />
              <h4 className="font-semibold text-slate-800">{perfil.nome}</h4>
            </div>
            <p className="text-sm text-slate-500 mb-3">{perfil.descricao}</p>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Users className="h-3 w-3" />
              <span>{perfil.usuarios} usuários</span>
              <span className="mx-1">•</span>
              <span>{perfil.permissoes} permissões</span>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEdit?.(perfil); }}>
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Card de Unidade
const UnidadeCard = ({ unidade, onEdit }) => {
  return (
    <Card className="hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
            <Building2 className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-slate-800">{unidade.nome}</h4>
              {unidade.principal && (
                <Badge className="bg-violet-100 text-violet-700">Principal</Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1">{unidade.endereco}</p>
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
              <span>{unidade.turmas} turmas</span>
              <span>{unidade.alunos} alunos</span>
              <span>{unidade.professores} professores</span>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => onEdit?.(unidade)}>
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Card de Integração
const IntegracaoCard = ({ integracao, onConfigure, onToggle }) => {
  return (
    <Card className={cn(
      "hover:shadow-md transition-all",
      integracao.ativa && "border-emerald-200"
    )}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={cn(
            "w-12 h-12 rounded-lg flex items-center justify-center",
            integracao.ativa ? "bg-emerald-100" : "bg-slate-100"
          )}>
            {integracao.icone === 'whatsapp' && <Phone className={cn("h-6 w-6", integracao.ativa ? "text-emerald-600" : "text-slate-400")} />}
            {integracao.icone === 'email' && <Mail className={cn("h-6 w-6", integracao.ativa ? "text-emerald-600" : "text-slate-400")} />}
            {integracao.icone === 'webhook' && <Webhook className={cn("h-6 w-6", integracao.ativa ? "text-emerald-600" : "text-slate-400")} />}
            {integracao.icone === 'database' && <Database className={cn("h-6 w-6", integracao.ativa ? "text-emerald-600" : "text-slate-400")} />}
            {integracao.icone === 'cloud' && <Cloud className={cn("h-6 w-6", integracao.ativa ? "text-emerald-600" : "text-slate-400")} />}
            {!integracao.icone && <Link2 className={cn("h-6 w-6", integracao.ativa ? "text-emerald-600" : "text-slate-400")} />}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-slate-800">{integracao.nome}</h4>
              <Badge className={integracao.ativa ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>
                {integracao.ativa ? 'Conectado' : 'Desconectado'}
              </Badge>
            </div>
            <p className="text-sm text-slate-500 mt-1">{integracao.descricao}</p>
            {integracao.ultimaSync && (
              <p className="text-xs text-slate-400 mt-2">
                Última sincronização: {integracao.ultimaSync}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Switch 
              checked={integracao.ativa} 
              onCheckedChange={() => onToggle?.(integracao)}
            />
            <Button variant="outline" size="sm" onClick={() => onConfigure?.(integracao)}>
              Configurar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Card de Log
const LogCard = ({ log }) => {
  const tipoConfig = {
    info: { icon: AlertCircle, color: 'text-blue-600 bg-blue-100' },
    sucesso: { icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-100' },
    erro: { icon: X, color: 'text-rose-600 bg-rose-100' },
    aviso: { icon: AlertCircle, color: 'text-amber-600 bg-amber-100' }
  };

  const config = tipoConfig[log.tipo] || tipoConfig.info;
  const Icon = config.icon;

  return (
    <div className="flex items-start gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors">
      <div className={cn("w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0", config.color)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700">{log.mensagem}</p>
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
          <span>{log.usuario}</span>
          <span>•</span>
          <span>{log.modulo}</span>
          <span>•</span>
          <span>{new Date(log.data).toLocaleString('pt-BR')}</span>
        </div>
      </div>
    </div>
  );
};

// ================== COMPONENTE PRINCIPAL ==================

export default function Configuracoes() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('usuarios');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [showNovoUsuario, setShowNovoUsuario] = useState(false);
  const [showNovaUnidade, setShowNovaUnidade] = useState(false);
  
  // Dados
  const [usuarios, setUsuarios] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [integracoes, setIntegracoes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [estatisticas, setEstatisticas] = useState({
    totalUsuarios: 0,
    usuariosAtivos: 0,
    totalUnidades: 0,
    integracoesAtivas: 0
  });

  // Form novo usuário
  const [novoUsuario, setNovoUsuario] = useState({
    nome: '',
    email: '',
    perfil: '',
    senha: ''
  });

  // Carregar dados
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Carregar usuários reais
      const usuariosRes = await fetch(`${API_URL}/api/users`).then(r => r.json()).catch(() => []);
      const usuariosArray = Array.isArray(usuariosRes) ? usuariosRes : [];
      
      // Mapear usuários para o formato esperado
      const usuariosMapeados = usuariosArray.map(u => ({
        id: u.id || u._id,
        nome: u.name || u.nome || 'Usuário',
        email: u.email,
        perfil: u.role || u.perfil || 'admin',
        ativo: u.active !== false
      }));
      setUsuarios(usuariosMapeados);

      // Dados simulados para demonstração
      const perfisMock = [
        { id: '1', nome: 'Diretoria', descricao: 'Acesso total ao sistema', usuarios: 2, permissoes: 45 },
        { id: '2', nome: 'Financeiro', descricao: 'Gestão financeira e cobrança', usuarios: 3, permissoes: 28 },
        { id: '3', nome: 'Coordenação', descricao: 'Gestão acadêmica e pedagógica', usuarios: 5, permissoes: 32 },
        { id: '4', nome: 'Professor', descricao: 'Lançamento de notas e frequência', usuarios: 25, permissoes: 15 },
        { id: '5', nome: 'Secretaria', descricao: 'Atendimento e documentação', usuarios: 4, permissoes: 20 },
        { id: '6', nome: 'Família', descricao: 'Portal do responsável', usuarios: 450, permissoes: 8 }
      ];

      const unidadesMock = [
        { id: '1', nome: 'Unidade Centro', endereco: 'Rua Principal, 123 - Centro', principal: true, turmas: 20, alunos: 450, professores: 25 },
        { id: '2', nome: 'Unidade Norte', endereco: 'Av. Norte, 456 - Zona Norte', principal: false, turmas: 12, alunos: 280, professores: 15 }
      ];

      const periodosMock = [
        { id: '1', nome: '2026', inicio: '2026-02-01', fim: '2026-12-20', ativo: true },
        { id: '2', nome: '2025', inicio: '2025-02-01', fim: '2025-12-20', ativo: false },
        { id: '3', nome: '2024', inicio: '2024-02-01', fim: '2024-12-20', ativo: false }
      ];

      const integracoesMock = [
        { id: '1', nome: 'WhatsApp Business', descricao: 'Envio de mensagens e notificações', icone: 'whatsapp', ativa: true, ultimaSync: 'Há 5 minutos' },
        { id: '2', nome: 'Email (SMTP)', descricao: 'Envio de emails transacionais', icone: 'email', ativa: true, ultimaSync: 'Há 2 minutos' },
        { id: '3', nome: 'Google Drive', descricao: 'Backup e armazenamento de arquivos', icone: 'cloud', ativa: false, ultimaSync: null },
        { id: '4', nome: 'Prefeitura (NFS-e)', descricao: 'Emissão de notas fiscais', icone: 'webhook', ativa: true, ultimaSync: 'Há 1 hora' },
        { id: '5', nome: 'Banco (Boletos)', descricao: 'Geração e baixa de boletos', icone: 'database', ativa: true, ultimaSync: 'Há 30 minutos' }
      ];

      const logsMock = [
        { id: '1', tipo: 'sucesso', mensagem: 'Usuário "Maria Silva" criado com sucesso', usuario: 'Admin', modulo: 'Usuários', data: new Date().toISOString() },
        { id: '2', tipo: 'info', mensagem: 'Backup automático realizado', usuario: 'Sistema', modulo: 'Backup', data: new Date(Date.now() - 3600000).toISOString() },
        { id: '3', tipo: 'aviso', mensagem: 'Tentativa de login falha para usuario@escola.com', usuario: 'Sistema', modulo: 'Segurança', data: new Date(Date.now() - 7200000).toISOString() },
        { id: '4', tipo: 'erro', mensagem: 'Falha na sincronização com Google Drive', usuario: 'Sistema', modulo: 'Integrações', data: new Date(Date.now() - 86400000).toISOString() },
        { id: '5', tipo: 'sucesso', mensagem: 'Permissões do perfil "Professor" atualizadas', usuario: 'Admin', modulo: 'Permissões', data: new Date(Date.now() - 172800000).toISOString() }
      ];

      setPerfis(perfisMock);
      setUnidades(unidadesMock);
      setPeriodos(periodosMock);
      setIntegracoes(integracoesMock);
      setLogs(logsMock);

      setEstatisticas({
        totalUsuarios: usuariosMapeados.length || perfisMock.reduce((sum, p) => sum + p.usuarios, 0),
        usuariosAtivos: usuariosMapeados.filter(u => u.ativo).length || perfisMock.reduce((sum, p) => sum + p.usuarios, 0) - 5,
        totalUnidades: unidadesMock.length,
        integracoesAtivas: integracoesMock.filter(i => i.ativa).length
      });

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const handleEditUsuario = (usuario) => {
    toast.info(`Editando usuário: ${usuario.nome}`);
  };

  const handleToggleUsuarioStatus = (usuario) => {
    setUsuarios(prev => prev.map(u => 
      u.id === usuario.id ? { ...u, ativo: !u.ativo } : u
    ));
    toast.success(`Usuário ${usuario.ativo ? 'desativado' : 'ativado'}`);
  };

  const handleResetPassword = (usuario) => {
    toast.success(`Email de redefinição enviado para ${usuario.email}`);
  };

  const handleEditPerfil = (perfil) => {
    toast.info(`Editando perfil: ${perfil.nome}`);
  };

  const handleViewPerfil = (perfil) => {
    toast.info(`Visualizando permissões de: ${perfil.nome}`);
  };

  const handleEditUnidade = (unidade) => {
    toast.info(`Editando unidade: ${unidade.nome}`);
  };

  const handleConfigureIntegracao = (integracao) => {
    toast.info(`Configurando: ${integracao.nome}`);
  };

  const handleToggleIntegracao = (integracao) => {
    setIntegracoes(prev => prev.map(i => 
      i.id === integracao.id ? { ...i, ativa: !i.ativa } : i
    ));
    toast.success(`${integracao.nome} ${integracao.ativa ? 'desconectado' : 'conectado'}`);
  };

  const handleCriarUsuario = () => {
    if (!novoUsuario.nome || !novoUsuario.email || !novoUsuario.perfil) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    toast.success('Usuário criado com sucesso!');
    setShowNovoUsuario(false);
    setNovoUsuario({ nome: '', email: '', perfil: '', senha: '' });
    loadData();
  };

  // Filtrar usuários
  const usuariosFiltrados = usuarios.filter(u =>
    u.nome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Configurações</h1>
          <p className="text-slate-500">Administração e configurações do sistema</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <ConfigStatCard title="Usuários" value={estatisticas.totalUsuarios} icon={Users} color="violet" />
        <ConfigStatCard title="Ativos" value={estatisticas.usuariosAtivos} icon={CheckCircle2} color="emerald" />
        <ConfigStatCard title="Unidades" value={estatisticas.totalUnidades} icon={Building2} color="blue" />
        <ConfigStatCard title="Integrações" value={estatisticas.integracoesAtivas} icon={Link2} color="amber" />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="usuarios" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Usuários
            </TabsTrigger>
            <TabsTrigger value="perfis" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Perfis
            </TabsTrigger>
            <TabsTrigger value="unidades" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Unidades
            </TabsTrigger>
            <TabsTrigger value="periodos" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Períodos
            </TabsTrigger>
            <TabsTrigger value="integracoes" className="flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Integrações
            </TabsTrigger>
            <TabsTrigger value="logs" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Logs
            </TabsTrigger>
          </TabsList>

          {activeTab === 'usuarios' && (
            <div className="flex gap-2">
              <Input
                placeholder="Buscar usuários..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64"
              />
              <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setShowNovoUsuario(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Novo Usuário
              </Button>
            </div>
          )}
        </div>

        {/* Tab: Usuários */}
        <TabsContent value="usuarios" className="space-y-4">
          {usuariosFiltrados.length === 0 ? (
            <Card className="py-12">
              <CardContent className="text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nenhum usuário encontrado</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {usuariosFiltrados.map((usuario) => (
                <UsuarioCard
                  key={usuario.id}
                  usuario={usuario}
                  onEdit={handleEditUsuario}
                  onToggleStatus={handleToggleUsuarioStatus}
                  onResetPassword={handleResetPassword}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab: Perfis e Permissões */}
        <TabsContent value="perfis" className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-700">Perfis de Acesso</h3>
            <Button className="bg-violet-600 hover:bg-violet-700">
              <Plus className="h-4 w-4 mr-2" />
              Novo Perfil
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {perfis.map((perfil) => (
              <PerfilCard
                key={perfil.id}
                perfil={perfil}
                onEdit={handleEditPerfil}
                onView={handleViewPerfil}
              />
            ))}
          </div>

          {/* Permissões Rápidas */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">Matriz de Permissões</CardTitle>
              <CardDescription>Visão geral das permissões por perfil</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Módulo</th>
                      <th className="text-center py-2 px-3">Diretoria</th>
                      <th className="text-center py-2 px-3">Financeiro</th>
                      <th className="text-center py-2 px-3">Coordenação</th>
                      <th className="text-center py-2 px-3">Professor</th>
                      <th className="text-center py-2 px-3">Secretaria</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['Dashboard', 'Alunos', 'Acadêmico', 'Financeiro', 'Comunicação', 'Relatórios', 'Configurações'].map((modulo) => (
                      <tr key={modulo} className="border-b border-slate-100">
                        <td className="py-2 px-3 font-medium">{modulo}</td>
                        <td className="text-center py-2 px-3"><CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" /></td>
                        <td className="text-center py-2 px-3">
                          {['Dashboard', 'Financeiro', 'Relatórios'].includes(modulo) 
                            ? <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
                            : <X className="h-4 w-4 text-slate-300 mx-auto" />
                          }
                        </td>
                        <td className="text-center py-2 px-3">
                          {['Dashboard', 'Alunos', 'Acadêmico', 'Comunicação', 'Relatórios'].includes(modulo)
                            ? <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
                            : <X className="h-4 w-4 text-slate-300 mx-auto" />
                          }
                        </td>
                        <td className="text-center py-2 px-3">
                          {['Dashboard', 'Acadêmico'].includes(modulo)
                            ? <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
                            : <X className="h-4 w-4 text-slate-300 mx-auto" />
                          }
                        </td>
                        <td className="text-center py-2 px-3">
                          {['Dashboard', 'Alunos', 'Comunicação'].includes(modulo)
                            ? <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
                            : <X className="h-4 w-4 text-slate-300 mx-auto" />
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Unidades */}
        <TabsContent value="unidades" className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-700">Unidades Escolares</h3>
            <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setShowNovaUnidade(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nova Unidade
            </Button>
          </div>

          <div className="space-y-4">
            {unidades.map((unidade) => (
              <UnidadeCard
                key={unidade.id}
                unidade={unidade}
                onEdit={handleEditUnidade}
              />
            ))}
          </div>
        </TabsContent>

        {/* Tab: Períodos Letivos */}
        <TabsContent value="periodos" className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-700">Períodos Letivos</h3>
            <Button className="bg-violet-600 hover:bg-violet-700">
              <Plus className="h-4 w-4 mr-2" />
              Novo Período
            </Button>
          </div>

          <div className="space-y-3">
            {periodos.map((periodo) => (
              <Card key={periodo.id} className={cn(
                "hover:shadow-md transition-all",
                periodo.ativo && "border-emerald-200 bg-emerald-50/30"
              )}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "w-12 h-12 rounded-lg flex items-center justify-center",
                        periodo.ativo ? "bg-emerald-100" : "bg-slate-100"
                      )}>
                        <Calendar className={cn(
                          "h-6 w-6",
                          periodo.ativo ? "text-emerald-600" : "text-slate-400"
                        )} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-slate-800">{periodo.nome}</h4>
                          {periodo.ativo && (
                            <Badge className="bg-emerald-100 text-emerald-700">Ativo</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {new Date(periodo.inicio).toLocaleDateString('pt-BR')} até {new Date(periodo.fim).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!periodo.ativo && (
                        <Button variant="outline" size="sm">
                          Ativar
                        </Button>
                      )}
                      <Button variant="ghost" size="icon">
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tab: Integrações */}
        <TabsContent value="integracoes" className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-700">Integrações e APIs</h3>
            <Button variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Nova Integração
            </Button>
          </div>

          <div className="space-y-4">
            {integracoes.map((integracao) => (
              <IntegracaoCard
                key={integracao.id}
                integracao={integracao}
                onConfigure={handleConfigureIntegracao}
                onToggle={handleToggleIntegracao}
              />
            ))}
          </div>

          {/* Webhooks */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">Webhooks</CardTitle>
              <CardDescription>Configure callbacks para eventos do sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-700">Novo Pagamento</p>
                    <p className="text-sm text-slate-500">Disparado quando um pagamento é registrado</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-slate-200 px-2 py-1 rounded">https://api.exemplo.com/webhook</code>
                    <Switch defaultChecked />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-700">Nova Matrícula</p>
                    <p className="text-sm text-slate-500">Disparado quando um aluno é matriculado</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Não configurado</Badge>
                    <Button variant="outline" size="sm">Configurar</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Logs */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Logs do Sistema</CardTitle>
                  <CardDescription>Histórico de atividades e eventos</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select defaultValue="todos">
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filtrar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="sucesso">Sucesso</SelectItem>
                      <SelectItem value="erro">Erros</SelectItem>
                      <SelectItem value="aviso">Avisos</SelectItem>
                      <SelectItem value="info">Info</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Exportar
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                <div className="space-y-1">
                  {logs.map((log) => (
                    <LogCard key={log.id} log={log} />
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Novo Usuário */}
      <Dialog open={showNovoUsuario} onOpenChange={setShowNovoUsuario}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-violet-600" />
              Novo Usuário
            </DialogTitle>
            <DialogDescription>
              Adicione um novo usuário ao sistema
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Nome completo *</Label>
              <Input
                value={novoUsuario.nome}
                onChange={(e) => setNovoUsuario({ ...novoUsuario, nome: e.target.value })}
                placeholder="Ex: Maria Silva"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={novoUsuario.email}
                onChange={(e) => setNovoUsuario({ ...novoUsuario, email: e.target.value })}
                placeholder="Ex: maria@escola.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Perfil de acesso *</Label>
              <Select 
                value={novoUsuario.perfil} 
                onValueChange={(v) => setNovoUsuario({ ...novoUsuario, perfil: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione o perfil" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="diretoria">Diretoria</SelectItem>
                  <SelectItem value="financeiro">Financeiro</SelectItem>
                  <SelectItem value="coordenacao">Coordenação</SelectItem>
                  <SelectItem value="professor">Professor</SelectItem>
                  <SelectItem value="secretaria">Secretaria</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Senha temporária</Label>
              <Input
                type="password"
                value={novoUsuario.senha}
                onChange={(e) => setNovoUsuario({ ...novoUsuario, senha: e.target.value })}
                placeholder="Deixe em branco para gerar automaticamente"
                className="mt-1"
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <p className="text-sm text-blue-700">
                Um email será enviado ao usuário com as instruções de acesso.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoUsuario(false)}>
              Cancelar
            </Button>
            <Button className="bg-violet-600 hover:bg-violet-700" onClick={handleCriarUsuario}>
              <UserPlus className="h-4 w-4 mr-2" />
              Criar Usuário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
